import { DATE_FORMAT, IGuestDetails, StringUtils, useConfirmContext } from '@localina/core';
import { FavoriteIcon } from '@localina/icons';
import { DateTime } from 'luxon';
import React from 'react';
import { useTranslation } from 'react-i18next';

interface IGuestDetailsProps {
    details: IGuestDetails;
}

interface IFieldProps {
    label: string;
    children: React.ReactNode;
}

const Field: React.FC<IFieldProps> = ({ label, children }) => (
    <div className="guest-field">
        <span className="guest-field__label">{label}</span>
        {children}
    </div>
);

const GuestDetails = ({ details }: IGuestDetailsProps) => {
    const { t } = useTranslation();
    const { copyOrLinkDialog } = useConfirmContext();

    const handlePhoneNumberClick = (phone: string) => () => {
        copyOrLinkDialog({ type: 'phoneNumber', value: phone });
    };
    const handleEmailClick = () => {
        if (details.guest.email) {
            copyOrLinkDialog({ type: 'email', value: details.guest.email });
        }
    };

    const getDate = (isoString: string) => {
        const datetime = DateTime.fromISO(isoString, { zone: 'utc' }).toLocal();
        return datetime.isValid ? datetime.toFormat(DATE_FORMAT) : '';
    };

    const { guest } = details;
    const salutationTranslationKey = `guest.details.view.fields.salutation.${String(guest.salutation).toLowerCase()}`;

    return (
        <div className="guest-fields">
            {}
            <div className="guest-fields__pair">
                <Field label={t('guest.details.view.fields.lastName')}>
                    <span className="guest-field__value">{guest.lastName}</span>
                </Field>
                {Boolean(guest.firstName) && (
                    <Field label={t('guest.details.view.fields.firstName')}>
                        <span className="guest-field__value">{guest.firstName}</span>
                    </Field>
                )}
            </div>

            {Boolean(guest.email) && (
                <Field label={t('guest.details.view.fields.email')}>
                    {}
                    <span className="guest-field__value is-action" onClick={handleEmailClick}>
                        {guest.email}
                    </span>
                </Field>
            )}

            {Boolean(guest.company) && (
                <Field label={t('guest.details.view.fields.company')}>
                    <span className="guest-field__value">{guest.company}</span>
                </Field>
            )}

            {guest.phoneNumbers.length > 0 && (
                <Field
                    label={t('guest.details.view.fields.phoneNumbers', {
                        count: guest.phoneNumbers.length,
                    })}
                >
                    {guest.phoneNumbers.map((phoneNumber, index) => (
                        <span
                            key={phoneNumber}
                            className="guest-field__value is-action"
                            onClick={handlePhoneNumberClick(phoneNumber)}
                        >
                            {StringUtils.formatPhoneNumber(phoneNumber)}
                            {}
                            {index === 0 && <FavoriteIcon className="guest-field__favorite" />}
                        </span>
                    ))}
                </Field>
            )}

            {Boolean(guest.salutation) && (
                <Field label={t('guest.details.view.fields.salutationLabel')}>
                    <span className="guest-field__value">{t(salutationTranslationKey)}</span>
                </Field>
            )}

            {Boolean(guest.allergies) && (
                <Field label={t('guest.details.view.fields.allergies')}>
                    <span className="guest-field__value is-multiline">{guest.allergies}</span>
                </Field>
            )}

            <div className="guest-fields__pair">
                {Boolean(details.firstReservationDateTime) && (
                    <Field label={t('guest.details.fields.firstReservation')}>
                        <span className="guest-field__value">{getDate(details.firstReservationDateTime)}</span>
                    </Field>
                )}
                {Boolean(details.lastReservationDateTime) && (
                    <Field label={t('guest.details.fields.lastReservation')}>
                        <span className="guest-field__value">{getDate(details.lastReservationDateTime)}</span>
                    </Field>
                )}
            </div>
        </div>
    );
};

export default GuestDetails;
